import React, { useCallback, useState } from 'react'
import classNames from 'classnames'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router'
import { ListGroup, Col, Container, Button } from 'react-bootstrap'
import { faPen, faTrash, faGripLines } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTranslation } from 'react-i18next'

import { BlockIcon } from 'components/BlockIcon'
import CommonToolTip from 'components/ui/tooltip/commonToolTip'
import { IBlockInstanceModel } from 'models/block.model'
import { useScormLoadPercentage } from 'helpers/scormLoadPercentage'
import * as fromBlockActions from 'store/actions/blocks.action'
import { BlockTypes } from 'enum/block-type.enum'
import { Tooltip } from './Tooltip'
import { Confirmation } from './Confirmation'
import styles from './BlockListItem.module.scss'

interface BlockListItemProps {
  id?: number
  name?: string
  dragDrop: boolean
  onEdit: any
  onDelete: any
  type?: BlockTypes | null
  disabled?: boolean
  destroyable?: boolean
  isValidated?: boolean
  processed?: boolean
  block: IBlockInstanceModel
  hasResponses?: boolean
}

const blocksWarningsEditing = new Set([
  BlockTypes.BlockEvaluation,
  BlockTypes.BlockSurvey,
])

const BlockListItem: React.FC<BlockListItemProps> = ({
  id,
  name,
  dragDrop,
  type,
  onDelete,
  onEdit,
  disabled = false,
  destroyable = true,
  isValidated = false,
  processed = false,
  block,
  hasResponses,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { lessonId } = useParams<{ lessonId: string }>()
  const [confirmationCallback, setConfirmationCallbackBase] = useState<
    (() => void) | null
  >(null)

  const setConfirmationCallback = (callback: () => void) => {
    const showConfirmation = type && blocksWarningsEditing.has(type)
    if (hasResponses && showConfirmation)
      return setConfirmationCallbackBase(() => () => callback())
    callback()
  }

  const onFinish = useCallback(() => {
    dispatch({ type: fromBlockActions.GET_LESSON_BLOCKS_LIST, lessonId })
  }, [dispatch, lessonId])
  const { showPercentage, percentage } = useScormLoadPercentage({
    block: {
      loadPercentage: block.loadPercentage,
      processing: block.processing,
      type: block.blockType,
      id: block.blockInstanceableId,
    },
    onFinish: onFinish,
  })

  return (
    <ListGroup.Item
      className={`${styles.listItem} ${dragDrop ? styles.draggable : ''}`}
    >
      <Container className={'row py-1 align-items-center'}>
        <Col xs={1} className={`px-0 d-flex align-items-center ${styles.icon}`}>
          {dragDrop ? (
            <FontAwesomeIcon icon={faGripLines} className={styles.dragIcon} />
          ) : (
            <BlockIcon type={type} />
          )}
        </Col>
        <Col className="px-0 overflow-hidden d-flex align-items-end">
          <p className={classNames(styles.name, 'text-truncate mr-1')}>
            {name}
            {type === BlockTypes.BlockScorm &&
              (!isValidated || !processed || showPercentage) && (
                <Tooltip {...{ isValidated, block }} />
              )}
          </p>
        </Col>
        <Col className={'col-auto px-0'}>
          <div className="d-flex justify-content-end align-items-center">
            <Button
              onClick={() => setConfirmationCallback(() => onEdit(id))}
              disabled={dragDrop || disabled}
            >
              <FontAwesomeIcon size={'xs'} icon={faPen} />
            </Button>
            <CommonToolTip
              text={t('administration.blocks.hasResponses')}
              disabled={!hasResponses}
              position="top"
            >
              <div>
                <Button
                  className={`ml-2`}
                  onClick={() => destroyable && onDelete(id)}
                  disabled={
                    dragDrop ||
                    disabled ||
                    showPercentage ||
                    !destroyable ||
                    (type === BlockTypes.BlockScorm &&
                      !processed &&
                      !!block.file?.id &&
                      !!block.processing)
                  }
                >
                  <FontAwesomeIcon size={'xs'} icon={faTrash} />
                </Button>
              </div>
            </CommonToolTip>
          </div>
        </Col>
        {showPercentage && percentage !== undefined && (
          <Col xs={12} className="px-0 mt-2">
            <div className="progress">
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                role="progressbar"
                style={{
                  width: `${percentage}%`,
                  minWidth: '30px',
                }}
              >
                {percentage}%
              </div>
            </div>
          </Col>
        )}
      </Container>
      <Confirmation
        show={!!confirmationCallback}
        onAccept={() => {
          confirmationCallback?.()
          setConfirmationCallbackBase(null)
        }}
        onClose={() => setConfirmationCallbackBase(null)}
      />
    </ListGroup.Item>
  )
}

export default BlockListItem
